/**
 * SharePipelinePopover — popover for sharing the current pipeline via URL.
 *
 * Generates a compressed share URL using lz-string and copies it to clipboard.
 * Includes an opt-in toggle for including input data with a privacy warning.
 */

import { AlertTriangle,Check, Copy, Share2 } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { generateShareUrl,serializePipeline } from '@/lib/pipelineSharing';
import { cn,copyToClipboard } from '@/lib/utils';
import { useEditorStore } from '@/store/useEditorStore';
import { toast } from '@/store/useToastStore';

export interface SharePipelinePopoverProps {
  /** Variant: 'default' (normal button) or 'compact' (small icon-only button for pipeline bar) */
  variant?: 'default' | 'compact';
}

export const SharePipelinePopover: React.FC<SharePipelinePopoverProps> = ({ variant = 'default' }) => {
  const { t } = useTranslation('pipeline');
  const pipeline = useEditorStore((s) => s.pipeline);
  const pipelineName = useEditorStore((s) => s.activePipelineName);
  const pipelineVariables = useEditorStore((s) => s.pipelineVariables);
  const inputData = useEditorStore((s) => s.input);

  const [open, setOpen] = useState(false);
  const [includeInput, setIncludeInput] = useState(false);
  const [copied, setCopied] = useState(false);

  const isEmpty = pipeline.length === 0;

  const shareUrl = useMemo(() => {
    if (isEmpty) return '';
    const compressed = serializePipeline({
      steps: pipeline,
      name: pipelineName ?? undefined,
      variables: Object.keys(pipelineVariables).length > 0 ? pipelineVariables : undefined,
      inputData: includeInput ? inputData : undefined,
    });
    return generateShareUrl(compressed);
  }, [pipeline, pipelineName, pipelineVariables, inputData, includeInput, isEmpty]);

  const charCount = shareUrl.length;

  const sizeHint = useMemo(() => {
    if (charCount === 0) return '';
    if (charCount < 500) return t('share.dialog.sizeHints.small');
    if (charCount < 2000) return t('share.dialog.sizeHints.medium');
    if (charCount < 4000) return t('share.dialog.sizeHints.large');
    return t('share.dialog.sizeHints.veryLarge');
  }, [charCount, t]);

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(shareUrl);
    if (ok) {
      setCopied(true);
      toast(t('share.dialog.linkCopied'), 'success');
      setTimeout(() => setCopied(false), 2000);
    }
  }, [shareUrl]);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setCopied(false);
    }
  }, []);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {variant === 'compact' ? (
          <button
            className={cn(
              'h-5 px-2 text-[10px] rounded-[7px] border border-border',
              'text-muted-foreground hover:border-zinc-500 hover:text-foreground',
              'transition-all duration-150 flex items-center gap-1',
              isEmpty && 'opacity-50 cursor-not-allowed'
            )}
            disabled={isEmpty}
            title={isEmpty ? t('steps.addFirst') : t('share.tooltip')}
          >
            <Share2 className="h-3 w-3" />
          </button>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            disabled={isEmpty}
            title={isEmpty ? t('steps.addFirst') : t('share.tooltip')}
          >
            <Share2 className="h-3.5 w-3.5 mr-1.5" />
            {t('share.button')}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-4 pt-3 pb-2 border-b border-border">
          <h3 className="text-sm font-semibold">{t('share.dialog.title')}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('share.dialog.description')}
          </p>
        </div>

        <div className="p-4 space-y-3">
          {/* Include input data toggle */}
          <div className="flex items-start gap-3">
            <Switch
              id="include-input"
              checked={includeInput}
              onCheckedChange={setIncludeInput}
              className="mt-0.5"
            />
            <div className="flex-1 min-w-0">
              <label htmlFor="include-input" className="text-xs font-medium cursor-pointer">
                {t('share.dialog.includeInput')}
              </label>
              <div className="flex items-center gap-1 mt-0.5">
                <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" />
                <span className="text-[11px] text-muted-foreground leading-tight">
                  {t('share.dialog.inputWarning')}
                </span>
              </div>
            </div>
          </div>

          {/* URL preview */}
          <div className="space-y-1.5">
            <div
              className="w-full text-xs bg-muted/50 border border-border rounded-md px-3 py-2 font-mono break-all max-h-20 overflow-auto select-all"
              role="textbox"
              aria-readonly="true"
              aria-label="Share URL"
            >
              {shareUrl}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {t('share.dialog.charCount', { count: charCount, hint: sizeHint })}
            </div>
          </div>

          {/* Copy button */}
          <Button
            variant="secondary"
            size="sm"
            className="w-full"
            onClick={handleCopy}
            disabled={isEmpty}
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5 mr-1.5" />
                {t('common:status.copied')}
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5 mr-1.5" />
                {t('share.dialog.copyLink')}
              </>
            )}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
